/**
 * ゆめさくコースのクリアバッジ。
 *
 * 絵は分野ごとに 1 枚しかない。同じ分野のコースは右下の番号だけで見分ける。
 * 意匠の指示が「新規イラスト0枚」で、101 本ぶんの絵を描かないため。
 *
 * 状態は 3 つ。1 レッスンも終えていなければ灰色、途中なら色つき、
 * 全部終えたら金の輪と星が付く。
 */
import COURSE_ORDER from "./course-slugs.fixture.json";
import { FIELDS, fieldOfCourseSlug } from "./course-fields";

/**
 * 番号を振る並び。本番のコース一覧を控えたもの。
 *
 * 並びを控えているのは、コースが増えたときに既に取った人の番号が
 * 動かないようにするため。新しいコースは末尾に回る。
 * 控え直しは scripts/update-course-slugs-fixture.ts。
 */
export const COURSE_SLUG_ORDER: readonly string[] = COURSE_ORDER as string[];

export type CourseBadgeState = "locked" | "wip" | "done";

export type CourseInput = {
  id: string;
  slug: string;
  title: string;
};

export type CourseProgressInput = {
  courseId: string;
  completedLessons: number;
  totalLessons: number;
};

export type CourseBadge = {
  id: string;
  slug: string;
  title: string;
  /** その分野の中で何本目か。絵の右下に出る */
  number: number;
  state: CourseBadgeState;
  art: string;
  /** 「12 / 17 レッスン」 */
  note: string;
};

export type CourseFieldGroup = {
  field: string;
  title: string;
  badges: CourseBadge[];
  /** 全部終えたコースの数 */
  done: number;
  total: number;
};

export function courseFieldArt(
  field: string,
  state: CourseBadgeState,
  number: number,
): string {
  const query = new URLSearchParams({ state, number: String(number) });
  return `/badges/field/${field}.svg?${query.toString()}`;
}

/**
 * コースを分野ごとにまとめて、1 本ずつバッジにする。
 *
 * 番号は order の並び順で振る。並びを画面から渡すのは、コースが増えたときに
 * 既に取った人の番号が動かないようにするため。order に無い slug は
 * 後ろに回す (新しく足されたコース)。
 */
export function courseBadgeGroups(
  courses: readonly CourseInput[],
  progress: readonly CourseProgressInput[],
  order: readonly string[],
): CourseFieldGroup[] {
  const rank = new Map(order.map((slug, index) => [slug, index]));
  const progressById = new Map(progress.map((p) => [p.courseId, p]));

  const byField = new Map<string, CourseInput[]>();
  for (const course of courses) {
    const field = fieldOfCourseSlug(course.slug);
    if (!field) continue;
    const list = byField.get(field) ?? [];
    list.push(course);
    byField.set(field, list);
  }

  const groups: CourseFieldGroup[] = [];
  for (const field of FIELDS) {
    const list = byField.get(field.slug);
    if (!list || list.length === 0) continue;

    // order に無いものは末尾。同じ順位なら slug で決める (毎回同じ並びにする)
    const sorted = [...list].sort((a, b) => {
      const ra = rank.get(a.slug) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b.slug) ?? Number.MAX_SAFE_INTEGER;
      return ra - rb || a.slug.localeCompare(b.slug);
    });

    const badges = sorted.map((course, index) => {
      const found = progressById.get(course.id);
      const completed = found?.completedLessons ?? 0;
      const total = found?.totalLessons ?? 0;
      const state = badgeState(completed, total);
      const number = index + 1;
      return {
        id: course.id,
        slug: course.slug,
        title: course.title,
        number,
        state,
        art: courseFieldArt(field.slug, state, number),
        note: total > 0 ? `${completed} レッスン済み` : "未受講",
      } satisfies CourseBadge;
    });

    groups.push({
      field: field.slug,
      title: field.title,
      badges,
      done: badges.filter((badge) => badge.state === "done").length,
      total: badges.length,
    });
  }
  return groups;
}

/**
 * レッスンの進み具合から状態を決める。
 *
 * レッスンが 0 本のコースを done にしない。0/0 を「全部終えた」と読むと、
 * 中身がまだ入っていないコースが勝手にクリア済みになる。
 */
export function badgeState(
  completed: number,
  total: number,
): CourseBadgeState {
  if (total <= 0 || completed <= 0) return "locked";
  return completed >= total ? "done" : "wip";
}

/**
 * そのコースが分野の中で何本目かを返す。分からなければ 0。
 *
 * コース詳細ページのように 1 本しか手元に無い場面から使う。控えた並びだけで
 * 決まるので、コース一覧を引かなくても同じ番号が出る。
 */
export function courseNumberInField(slug: string): number {
  const field = fieldOfCourseSlug(slug);
  if (!field) return 0;
  const siblings = COURSE_SLUG_ORDER.filter(
    (candidate) => fieldOfCourseSlug(candidate) === field,
  );
  const index = siblings.indexOf(slug);
  return index < 0 ? 0 : index + 1;
}

/** 全分野を通したクリア数 */
export function clearedCount(groups: readonly CourseFieldGroup[]): number {
  return groups.reduce((sum, group) => sum + group.done, 0);
}

/** 全分野を通したコース数 */
export function courseCount(groups: readonly CourseFieldGroup[]): number {
  return groups.reduce((sum, group) => sum + group.total, 0);
}
